import { Link, useRouterState } from '@tanstack/react-router';
import {
  IcnBookmark,
  IcnComment,
  IcnDoc,
  IcnFolder,
  IcnHome,
  IcnStopwatch,
  IcnUser,
} from '@viquard/doc-ui';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';

interface NavItem {
  id: string;
  label: string;
  icon: ReactElement;
  to: string;
}

export interface TopbarNavProps {
  hidden?: boolean;
  authenticated?: boolean;
}

interface IndicatorState {
  /** Pixels from the nav's left edge to the active item's left edge. */
  x: number;
  /** Active item's width in px. */
  w: number;
  /** Hidden when no nav item matches the current route (e.g. /settings). */
  visible: boolean;
}

const ICON_SIZE = 14;
const HIDDEN_INDICATOR: IndicatorState = { x: 0, w: 0, visible: false };

/** Primary nav strip — DEMO/shell.jsx room switcher.
 *
 *  The active state is communicated by a single absolutely-positioned
 *  pill (`.topbar-nav__indicator`) that moves to the active item. We measure
 *  the active item's bounding rect against the nav container, then set
 *  `transform: translateX(...)` + `width: ...`.
 *  ResizeObserver re-measures on locale changes (i18n swap → labels grow
 *  / shrink) and on any container reflow. */
export function TopbarNav({ hidden, authenticated = false }: TopbarNavProps): ReactNode {
  const { t } = useTranslation('common');
  const { location } = useRouterState();
  const navRef = useRef<HTMLElement | null>(null);
  const itemRefs = useRef<Map<string, HTMLElement>>(new Map());
  const [indicator, setIndicator] = useState<IndicatorState>(HIDDEN_INDICATOR);
  const [hasMeasuredIndicator, setHasMeasuredIndicator] = useState(false);
  const [indicatorReady, setIndicatorReady] = useState(false);

  const items = useMemo<NavItem[]>(
    () => {
      const publicItems: NavItem[] = [
        {
          id: 'home',
          label: t('nav.home'),
          icon: <IcnHome size={ICON_SIZE} />,
          to: '/',
        },
        {
          id: 'articles',
          label: t('nav.articles'),
          icon: <IcnDoc size={ICON_SIZE} />,
          to: '/articles',
        },
        {
          id: 'forum',
          label: t('nav.forum'),
          icon: <IcnComment size={ICON_SIZE} />,
          to: '/forum',
        },
        {
          id: 'surveys',
          label: t('nav.surveys'),
          icon: <IcnBookmark size={ICON_SIZE} />,
          to: '/surveys',
        },
        {
          id: 'calendar',
          label: t('nav.calendar'),
          icon: <IcnStopwatch size={ICON_SIZE} />,
          to: '/calendar',
        },
        {
          id: 'groups',
          label: t('nav.groups'),
          icon: <IcnUser size={ICON_SIZE} />,
          to: '/groups',
        },
      ];
      if (!authenticated) return publicItems;
      return [
        {
          id: 'dashboard',
          label: t('nav.dashboard'),
          icon: <IcnHome size={ICON_SIZE} />,
          to: '/dashboard',
        },
        ...publicItems.slice(1),
        {
          id: 'library',
          label: t('nav.library'),
          icon: <IcnFolder size={ICON_SIZE} />,
          to: '/library',
        },
      ];
    },
    [authenticated, t],
  );

  const activeId = useMemo<string | null>(() => {
    const found = items.find((it) => matchActive(location.pathname, it.to));
    return found?.id ?? null;
  }, [items, location.pathname]);

  useEffect(() => {
    if (!hasMeasuredIndicator || indicatorReady) return undefined;
    let secondFrame = 0;
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        setIndicatorReady(true);
      });
    });
    return () => {
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
    };
  }, [hasMeasuredIndicator, indicatorReady]);

  const measure = useCallback(() => {
    const navEl = navRef.current;
    if (navEl === null) return;
    if (activeId === null) {
      // No matching item — fade the indicator out, keep its last
      // x/w so it doesn't visibly snap to (0, 0) under the fade.
      setIndicator((prev) => (prev.visible ? { ...prev, visible: false } : prev));
      return;
    }
    const itemEl = itemRefs.current.get(activeId);
    if (itemEl === undefined) return;
    const itemRect = itemEl.getBoundingClientRect();
    const navRect = navEl.getBoundingClientRect();
    const next: IndicatorState = {
      x: itemRect.left - navRect.left,
      w: itemRect.width,
      visible: true,
    };
    setHasMeasuredIndicator(true);
    setIndicator((prev) =>
      prev.x === next.x && prev.w === next.w && prev.visible === next.visible ? prev : next,
    );
  }, [activeId]);

  // Layout effect — re-measure synchronously after each render that
  // could move the active item. `useLayoutEffect` blocks paint, so the
  // user never sees a stale indicator position flash.
  useLayoutEffect(() => {
    measure();
  }, [measure]);

  // ResizeObserver — catches font swaps, locale changes (Chinese chars
  // are wider than Latin), window resizes, and DOM mutations from any
  // ancestor that nudges item widths.
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return undefined;
    const navEl = navRef.current;
    if (navEl === null) return undefined;
    const ro = new ResizeObserver(() => {
      measure();
    });
    ro.observe(navEl);
    for (const el of itemRefs.current.values()) ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, [measure]);

  if (hidden === true) return null;

  const setItemRef =
    (id: string) =>
    (el: HTMLElement | null): void => {
      if (el !== null) itemRefs.current.set(id, el);
      else itemRefs.current.delete(id);
    };

  return (
    <nav
      ref={navRef}
      aria-label={t('nav.primary')}
      className="topbar-nav"
      data-indicator-ready={indicatorReady ? 'true' : 'false'}
    >
      <span
        aria-hidden
        className="topbar-nav__indicator"
        style={{
          transform: `translateX(${String(indicator.x)}px)`,
          width: `${String(indicator.w)}px`,
          opacity: indicator.visible ? 1 : 0,
        }}
      />
      {items.map((item) => {
        const active = matchActive(location.pathname, item.to);
        return (
          <Link
            key={item.id}
            ref={setItemRef(item.id)}
            to={item.to}
            aria-current={active ? 'page' : undefined}
            className="topbar-nav-link"
          >
            <span aria-hidden className="topbar-nav-link__icon">
              {item.icon}
            </span>
            {item.label}
          </Link>
        );
      })}
    </nav>
  );
}

/** True when `currentPath` is on or under `target`. The dashboard is the
 *  Home destination — match `/dashboard` only when on it. Groups +
 *  Articles match the index AND any detail (`/groups/<slug>`,
 *  `/articles/<slug>`) so the rail stays lit while the user drills in. */
function matchActive(currentPath: string, target: string): boolean {
  if (target === '/dashboard') return currentPath === '/dashboard';
  if (target === '/') return currentPath === '/';
  return currentPath === target || currentPath.startsWith(`${target}/`);
}
